import type { LocationNode } from '../api/types'

function walk(nodes: LocationNode[], trail: string[], paths: Map<string, string>, names: Map<string, string>) {
  for (const node of nodes) {
    const next = [...trail, node.name]
    paths.set(node.unique_id, next.join(' / '))
    names.set(node.unique_id, node.name)
    walk(node.children, next, paths, names)
  }
}

/** Map location unique_id → breadcrumb path ("Sverige / BRF Dublonen / …"). */
export function locationPaths(nodes: LocationNode[]): Map<string, string> {
  const paths = new Map<string, string>()
  walk(nodes, [], paths, new Map())
  return paths
}

/** Map location unique_id → short name. */
export function locationNames(nodes: LocationNode[]): Map<string, string> {
  const names = new Map<string, string>()
  walk(nodes, [], new Map(), names)
  return names
}

/** The location uids that are grant-inheritance BARRIERS
 *  (inherits_grants === false) — pickers suffix their labels with the
 *  barrier glyph. */
export function locationBarriers(nodes: LocationNode[]): Set<string> {
  const out = new Set<string>()
  const collect = (ns: LocationNode[]) => {
    for (const n of ns) {
      if (n.inherits_grants === false) out.add(n.unique_id)
      collect(n.children)
    }
  }
  collect(nodes)
  return out
}

function findNode(nodes: LocationNode[], uid: string): LocationNode | null {
  for (const node of nodes) {
    if (node.unique_id === uid) return node
    const hit = findNode(node.children, uid)
    if (hit) return hit
  }
  return null
}

/** Every location uid at or under `uid`, including `uid` itself. */
export function locationDescendants(nodes: LocationNode[], uid: string): Set<string> {
  const out = new Set<string>()
  const parent = findNode(nodes, uid)
  if (!parent) return out
  const walk = (ns: LocationNode[]) => {
    for (const n of ns) {
      out.add(n.unique_id)
      walk(n.children)
    }
  }
  out.add(parent.unique_id)
  walk(parent.children)
  return out
}

/** The most common type among the node's existing children — the default
 * for a new sibling (e.g. an address full of floors suggests "floor").
 * Returns null when there are no children to infer from. */
export function mostCommonChildType(nodes: LocationNode[], parentUid: string): string | null {
  const parent = findNode(nodes, parentUid)
  if (!parent || parent.children.length === 0) return null
  const counts = new Map<string, number>()
  for (const child of parent.children) {
    counts.set(child.type, (counts.get(child.type) ?? 0) + 1)
  }
  let best: string | null = null
  let bestCount = 0
  for (const [type, count] of counts) {
    if (count > bestCount) {
      best = type
      bestCount = count
    }
  }
  return best
}
